import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { getAdminCMSFull, updateCMSContent } from '@/lib/api';
import { ImageUploadField, getImageUrl } from '@/components/ImageUploadField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Save, Globe, Image, Type, Clock } from 'lucide-react';

// ─── Time options ─────────────────────────────────────────────────
const TIMES = [];
for (let h = 7; h <= 22; h++) {
  TIMES.push(`${h}h00`);
  if (h < 22) TIMES.push(`${h}h30`);
}

const SECTIONS = [
  { key: 'hero',       label: 'Section Principale (Hero)',            icon: Globe, description: "Le grand visuel en haut de la page d'accueil" },
  { key: 'intro',      label: 'Section "Un espace dédié au beau"',    icon: Image, description: 'La section avec photo de la boutique' },
  { key: 'categories', label: 'Section Catégories',                   icon: Type,  description: 'Titre et sous-titre de la grille de catégories' },
  { key: 'featured',   label: 'Section Produits Vedettes',            icon: Type,  description: 'Titre et sous-titre de la sélection' },
  { key: 'cta',        label: 'Section "Visitez la boutique"',        icon: Type,  description: 'La bannière sombre en bas de page' },
  { key: 'contact',    label: 'Contact & Horaires',                   icon: Clock, description: 'Téléphone et horaires jour par jour' },
];

const SCHEDULE_DAYS = [
  { key: 'hours_lundi',    label: 'Lundi' },
  { key: 'hours_mardi',    label: 'Mardi' },
  { key: 'hours_mercredi', label: 'Mercredi' },
  { key: 'hours_jeudi',    label: 'Jeudi' },
  { key: 'hours_vendredi', label: 'Vendredi' },
  { key: 'hours_samedi',   label: 'Samedi' },
  { key: 'hours_dimanche', label: 'Dimanche' },
];

// ─── Parse / Serialize ─────────────────────────────────────────────
const normalizeTime = (t) => {
  if (!t) return '';
  t = t.trim();
  // "10h" → "10h00"
  if (/^\d{1,2}h$/.test(t)) return t + '00';
  return t;
};

function parseSchedule(value) {
  if (!value) return { closed: true, p1Start: '', p1End: '', p2Start: '', p2End: '' };
  const v = value.trim().toLowerCase();
  if (v === 'fermé' || v === 'ferme') {
    return { closed: true, p1Start: '', p1End: '', p2Start: '', p2End: '' };
  }
  const parts = value.split('/').map(s => s.trim());
  const parsePeriod = (s) => {
    if (!s) return ['', ''];
    const sides = s.split('-').map(t => normalizeTime(t.trim()));
    return [sides[0] || '', sides[1] || ''];
  };
  const [p1s, p1e] = parsePeriod(parts[0]);
  const [p2s, p2e] = parts[1] ? parsePeriod(parts[1]) : ['', ''];
  return { closed: false, p1Start: p1s, p1End: p1e, p2Start: p2s, p2End: p2e };
}

function serializeSchedule(sched) {
  if (sched.closed) return 'Fermé';
  const parts = [];
  if (sched.p1Start && sched.p1End) parts.push(`${sched.p1Start} - ${sched.p1End}`);
  if (sched.p2Start && sched.p2End) parts.push(`${sched.p2Start} - ${sched.p2End}`);
  return parts.length > 0 ? parts.join(' / ') : 'Fermé';
}

// ─── DayScheduleEditor ────────────────────────────────────────────
function TimeSelect({ value, onChange, placeholder = 'Heure', testId }) {
  return (
    <Select value={value || '__none__'} onValueChange={v => onChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className="w-24 text-sm" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">—</SelectItem>
        {TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function DayScheduleEditor({ dayKey, label, value, onChange }) {
  const sched = parseSchedule(value);

  const update = (field, val) => {
    const next = { ...sched, [field]: val };
    onChange(serializeSchedule(next));
  };

  const toggleClosed = () => {
    if (sched.closed) {
      onChange('10h00 - 19h00');
    } else {
      onChange('Fermé');
    }
  };

  const isClosed = sched.closed;

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${isClosed ? 'bg-red-50 border-red-200' : 'bg-white border-stone-200'}`}
      data-testid={`schedule-editor-${dayKey}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-sm text-foreground w-24">{label}</span>
        <button
          type="button"
          onClick={toggleClosed}
          className={`text-xs px-3 py-1 rounded-full border transition-colors font-medium ${
            isClosed
              ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-50'
              : 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
          }`}
          data-testid={`toggle-closed-${dayKey}`}
        >
          {isClosed ? 'Fermé' : 'Ouvert'}
        </button>
      </div>

      {!isClosed && (
        <div className="space-y-2">
          {/* Période 1 — Matin */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-24 shrink-0">Matin :</span>
            <TimeSelect
              value={sched.p1Start}
              onChange={v => update('p1Start', v)}
              testId={`${dayKey}-p1-start`}
            />
            <span className="text-xs text-muted-foreground">à</span>
            <TimeSelect
              value={sched.p1End}
              onChange={v => update('p1End', v)}
              testId={`${dayKey}-p1-end`}
            />
          </div>
          {/* Période 2 — Après-midi */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-24 shrink-0">Après-midi :</span>
            <TimeSelect
              value={sched.p2Start}
              onChange={v => update('p2Start', v)}
              testId={`${dayKey}-p2-start`}
            />
            <span className="text-xs text-muted-foreground">à</span>
            <TimeSelect
              value={sched.p2End}
              onChange={v => update('p2End', v)}
              testId={`${dayKey}-p2-end`}
            />
          </div>
          {/* Preview */}
          <p className="text-xs text-primary/70 font-medium pt-1">
            → {serializeSchedule(sched)}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main AdminCMS component ──────────────────────────────────────
export default function AdminCMS() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const token = localStorage.getItem('tapisstore_admin_token');
    if (token) fetchCMS();
    else setLoading(false);
  }, []);

  const fetchCMS = async () => {
    try {
      const response = await getAdminCMSFull();
      setItems(response.data || []);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) {
        const detail = status ? ` (erreur ${status})` : ' (serveur inaccessible)';
        toast.error(`Erreur lors du chargement du contenu${detail}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Rechargement si items est vide après le premier chargement
  const handleReload = () => {
    setLoading(true);
    fetchCMS();
  };

  const getValue = useCallback((key) => {
    const item = items.find(i => i.key === key);
    return item?.value ?? '';
  }, [items]);

  const setValue = useCallback((key, value) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, value } : i));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCMSContent(items.map(({ key, value, type, label, section }) => ({ key, value, type, label, section })));
      toast.success('Contenu sauvegardé !');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const SCHEDULE_KEYS_SET = new Set(SCHEDULE_DAYS.map(d => d.key));
  const sectionItems = items.filter(i =>
    i.section === activeSection &&
    i.key !== 'shop_hours' &&
    !SCHEDULE_KEYS_SET.has(i.key)
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-cms">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Gestion du contenu</h1>
            <p className="text-muted-foreground mt-1">Modifiez les textes et images de votre site</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-cms-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer tout
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Section navigation */}
          <div className="col-span-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {SECTIONS.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.key}
                      onClick={() => setActiveSection(section.key)}
                      className={`w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 transition-colors ${
                        activeSection === section.key
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted text-foreground'
                      }`}
                      data-testid={`cms-section-${section.key}`}
                    >
                      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                      <p className="text-sm font-medium leading-tight">{section.label}</p>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Content editor */}
          <div className="col-span-9">
            {SECTIONS.filter(s => s.key === activeSection).map((section) => (
              <Card key={section.key}>
                <CardHeader>
                  <CardTitle className="font-heading">{section.label}</CardTitle>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Aucun champ chargé → bouton de rechargement */}
                  {sectionItems.length === 0 && activeSection !== 'contact' && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="text-sm mb-3">Aucun champ disponible pour cette section.</p>
                      <button
                        onClick={handleReload}
                        className="text-xs text-primary underline hover:no-underline"
                        data-testid="cms-reload-btn"
                      >
                        Recharger le contenu
                      </button>
                    </div>
                  )}

                  {/* Standard fields */}
                  {sectionItems.map((item) => (
                    <div key={item.key}>
                      {item.type === 'image' ? (
                        <ImageUploadField
                          label={item.label}
                          value={item.value}
                          onChange={(val) => setValue(item.key, val)}
                          fieldId={`cms-${item.key}`}
                        />
                      ) : item.value?.length > 80 ? (
                        <div>
                          <Label htmlFor={`cms-${item.key}`}>{item.label}</Label>
                          <Textarea
                            id={`cms-${item.key}`}
                            value={item.value}
                            onChange={(e) => setValue(item.key, e.target.value)}
                            className="mt-1 min-h-[80px]"
                            data-testid={`cms-${item.key}-input`}
                          />
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor={`cms-${item.key}`}>{item.label}</Label>
                          <Input
                            id={`cms-${item.key}`}
                            value={item.value}
                            onChange={(e) => setValue(item.key, e.target.value)}
                            className="mt-1"
                            data-testid={`cms-${item.key}-input`}
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* ── Horaires jour par jour (section contact uniquement) ── */}
                  {activeSection === 'contact' && (
                    <div>
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border">
                        <Clock className="w-4 h-4 text-primary" />
                        <Label className="font-semibold">Horaires d'ouverture — jour par jour</Label>
                      </div>
                      <p className="text-xs text-muted-foreground mb-4">
                        Cliquez sur "Ouvert / Fermé" pour basculer. Sélectionnez 2 plages horaires par jour (matin + après-midi) ou une seule si la boutique est ouverte en continu.
                        Les jours avec les mêmes horaires seront regroupés dans le footer.
                      </p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {SCHEDULE_DAYS.map(({ key, label }) => (
                          <DayScheduleEditor
                            key={key}
                            dayKey={key}
                            label={label}
                            value={getValue(key)}
                            onChange={(val) => setValue(key, val)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hero preview */}
                  {activeSection === 'hero' && getValue('hero_image') && (
                    <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-dashed border-border">
                      <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wider">Aperçu Hero</p>
                      <div className="relative h-32 rounded overflow-hidden">
                        <img
                          key={getImageUrl(getValue('hero_image'))}
                          src={getImageUrl(getValue('hero_image'))}
                          alt="Aperçu hero"
                          className="w-full h-full object-cover admin-img-fade"
                        />
                        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
                          <p className="font-bold text-xl">{getValue('hero_title')}</p>
                          <p className="italic text-sm opacity-80">{getValue('hero_subtitle')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
